import React, { type JSX } from "react";

class TextService {

  private _levenshtein(a: string, b: string): number {
    const dp: number[][] = Array(a.length + 1)
      .fill(0)
      .map(() => Array(b.length + 1).fill(0));

    for (let i = 0; i <= a.length; i++) dp[i][0] = i;
    for (let j = 0; j <= b.length; j++) dp[0][j] = j;

    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        if (a[i - 1] === b[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1];
        } else {
          dp[i][j] =
            1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
        }
      }
    }

    return dp[a.length][b.length];
  }
  
  private _diffChars = (orig: string, trans: string, keyPrefix: string): JSX.Element[] => {
    const minLen = Math.min(orig.length, trans.length);
    const chars: JSX.Element[] = [];

    for (let i = 0; i < minLen; i++) {
      if (orig[i] === trans[i]) {
        chars.push(<React.Fragment key={`${keyPrefix}-same-${i}`}>{trans[i]}</React.Fragment>);
      } else {
        chars.push(<strong key={`${keyPrefix}-diff-${i}`}>{trans[i]}</strong>);
      }
    }

    if (trans.length > orig.length) {
      chars.push(
        <strong key={`${keyPrefix}-extra`}>{trans.slice(orig.length)}</strong>
      );
    }

    return chars;
  }

  public highlightDifferencesJSX = (
    original: string,
    corrected: string
  ): JSX.Element[] => {
    const origWords = original.trim().split(/\s+/);
    const transWords = corrected.trim().split(/\s+/);
    const result: JSX.Element[] = [];
    const threshold = 1;

    let i = 0, j = 0, wordIndex = 0;

    while (i < origWords.length || j < transWords.length) {
      const orig = origWords[i] || '';
      const trans = transWords[j] || '';
      const key = `word-${wordIndex++}`;

      if (orig === trans) {
        result.push(<React.Fragment key={key}>{trans} </React.Fragment>);
        i++; j++;
      } else if (!orig) {
        result.push(<strong key={key}>{trans} </strong>);
        j++;
      } else if (!trans) {
        i++;
      } else {
        const dist = this._levenshtein(orig, trans);
        if (dist <= threshold) {
          result.push(<span key={key}>{this._diffChars(orig, trans, key)} </span>);
        } else {
          result.push(<strong key={key}>{trans} </strong>);
        }
        i++; j++;
      }
    }

    return result;
  }
}

export default new TextService();